/**
 * AppShell — thin layout composition. All primary UI parts live in
 * /components/shell/*. This wrapper only:
 *   1. Reads the auth/branding/permission/addon contexts
 *   2. Computes role- + permission-filtered nav groups
 *   3. Wires Sidebar (desktop) + Header (all sizes) + <Outlet/>
 *   4. Hosts the ProfileDialog and QRCheckinDialog modals
 *
 * Original 371-line AppShell has been split in iter-53 into:
 *   - components/shell/navConfig.js  (route metadata)
 *   - components/shell/Sidebar.jsx   (desktop rail)
 *   - components/shell/Header.jsx    (top bar + mobile nav)
 *   - components/shell/NotificationBell.jsx
 */
import { Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldStar } from "@phosphor-icons/react";
import { useBranding } from "@/contexts/BrandingContext";
import { usePermissions } from "@/hooks/usePermissions";
import { useAddons } from "@/contexts/AddonsContext";
import { ROUTE_ROLES } from "@/lib/routeRoles";
import ProfileDialog from "@/components/ProfileDialog";
import Sidebar from "@/components/shell/Sidebar";
import Header from "@/components/shell/Header";
import { NAV_GROUPS, SUITE_ITEMS, SUITE_LABEL } from "@/components/shell/navConfig";

export default function AppShell() {
  const { user, org, activeRole, activeBranch, branches, logout, setActiveRole, setActiveBranch } = useAuth();
  const { brand } = useBranding();
  const { can } = usePermissions();
  const { hasEnterpriseWorkforce } = useAddons();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);

  const roles = user?.roles || [];
  const isSuperAdmin = user?.platform_role === "super_admin";

  // Filter primary nav groups per role + permission module. Owner
  // always passes via usePermissions().can short-circuit.
  const baseGroups = NAV_GROUPS
    .map(g => ({
      ...g,
      items: g.items.filter(i =>
        i.roles.includes(activeRole) &&
        (!i.module || can(i.module, "view"))
      ),
    }))
    .filter(g => g.items.length > 0);

  // Enterprise Workforce Suite — appended only when add-on is active.
  const suiteItems = SUITE_ITEMS.filter(i =>
    i.roles.includes(activeRole) && (!i.module || can(i.module, "view"))
  );
  const workforceGroup = (hasEnterpriseWorkforce && suiteItems.length > 0)
    ? [{ label: SUITE_LABEL, items: suiteItems, isEnterprise: true }]
    : [];

  const navGroups = [
    ...baseGroups,
    ...workforceGroup,
    ...(isSuperAdmin ? [{
      label: "Platform",
      items: [{
        to: "/platform",
        label: "Super Admin",
        icon: ShieldStar,
        roles: ROUTE_ROLES["/platform"] || [activeRole],
      }],
    }] : []),
  ];
  const flatNav = navGroups.flatMap(g => g.items);
  const brandInitial = (brand.brand_name || "T").trim().charAt(0).toUpperCase() || "T";

  const branchLabel = activeBranch === "all"
    ? "All Branches"
    : (branches.find(b => b.id === activeBranch)?.name || "Select branch");

  // iter-60 — Show QR / Scan QR buttons appear directly in the header
  // and are gated purely by (a) the ACTIVE branch being on QR attendance
  // and (b) the user holding the corresponding permission capability.
  // The old combined "Check-in" popup was removed.
  const qrBranch = (activeBranch && activeBranch !== "all")
    ? branches.find(b => b.id === activeBranch)
    : null;
  const branchHasQr = qrBranch?.attendance?.attendance_method === "qr";

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex bg-background">
      <Sidebar
        brand={brand}
        brandInitial={brandInitial}
        org={org}
        user={user}
        navGroups={navGroups}
        onOpenProfile={() => setProfileOpen(true)}
        onLogout={doLogout}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <Header
          brand={brand}
          org={org}
          user={user}
          roles={roles}
          activeRole={activeRole}
          activeBranch={activeBranch}
          branches={branches}
          branchLabel={branchLabel}
          branchHasQr={branchHasQr}
          flatNav={flatNav}
          onOpenProfile={() => setProfileOpen(true)}
          onLogout={doLogout}
          setActiveRole={setActiveRole}
          setActiveBranch={setActiveBranch}
        />

        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
          <Outlet />
        </main>
      </div>

      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
    </div>
  );
}
